
if (!customElements.get('description-expander')) {
  const DEFAULT_LINES = 3; 
  const MOBILE = '(max-width: 749px)';
  const CLIP = 'collection-hero__description-clip'; 
  const SPLIT = 'collection-hero__description-split'; 
  const MORE = 'collection-hero__description-more'; 

  customElements.define(
    'description-expander',
    class DescriptionExpander extends HTMLElement {
      constructor() {
        super();
        this.description = this.querySelector('.product__description, .collection-hero__description');
        this.button = this.querySelector('button');
        if (!this.description || !this.button) return;

        this.readMoreText = this.button.textContent;
        this.showLessText = this.dataset.showLessText;
        this.button.addEventListener('click', this.toggle.bind(this));

     
        this.inline = this.description.classList.contains('collection-hero__description');
        this.media = window.matchMedia(MOBILE);
        this.more = null;
      }

      connectedCallback() {
        if (!this.inline || this.listening) return;
        this.listening = true;

        let timer = null;
        this.onResize = () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.clamp(), 120);
        };
        this.onBreakpoint = () => this.clamp();

        window.addEventListener('resize', this.onResize);
     
        if (this.media.addEventListener) {
          this.media.addEventListener('change', this.onBreakpoint);
        } else if (this.media.addListener) {
          this.media.addListener(this.onBreakpoint);
        }

        this.clamp();

    
        this.fontsReady().then(() => this.clamp());

       
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this.clamp(), { once: true });
        }
      }

    
      fontsReady() {
        if (!document.fonts || !document.fonts.load) return Promise.resolve();

        const style = getComputedStyle(this.description);
        const faces = [
          style.fontStyle + ' ' + style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily,
          style.fontStyle + ' 700 ' + style.fontSize + ' ' + style.fontFamily /* bold runs in the copy */
        ];

        return Promise.all(faces.map((face) => document.fonts.load(face).catch(() => {})))
          .then(() => document.fonts.ready)
          .catch(() => {});
      }

      disconnectedCallback() {
        if (!this.listening) return;
        this.listening = false;
        window.removeEventListener('resize', this.onResize);
        if (this.media.removeEventListener) {
          this.media.removeEventListener('change', this.onBreakpoint);
        } else if (this.media.removeListener) {
          this.media.removeListener(this.onBreakpoint);
        }
      }

      toggle() {
        const expanding = this.description.classList.contains('collapsed');

        if (expanding) {
          this.restore(); 
          this.description.classList.remove('collapsed');
        } else {
          this.description.classList.add('collapsed');
        }

        this.button.setAttribute('aria-expanded', expanding);
        this.button.textContent = expanding ? this.showLessText : this.readMoreText;

        if (!expanding) this.clamp();
      }

     
      maxLines() {
        const declared = parseInt(
          getComputedStyle(this.description).getPropertyValue('--collection-hero-lines'),
          10
        );
        return declared > 0 ? declared : DEFAULT_LINES;
      }

      /* Cut the description to maxLines() lines, button inline at the end. */
      clamp() {
        if (!this.inline) return;

        this.restore();
        if (!this.description.classList.contains('collapsed')) return;

        const LINES = this.maxLines();

        /* Measure against the real line positions rather than the CSS cap. */
        this.description.classList.add('is-clipped');

        const lines = this.lineBoxes();
        if (lines.length <= LINES) return; /* short enough already: nothing to cut */

  
        const top = this.description.getBoundingClientRect().top;
        this.description.style.maxHeight = Math.round(lines[LINES].top - top) + 'px';

        const limit = lines[LINES - 1].bottom + 1; /* a pixel for sub-pixel rounding */
        let map = this.textMap();
        let cut = this.wordBoundary(map.text, this.lastIndexWithin(map, limit));

      
        for (let tries = 0; tries < 30 && cut > 0; tries++) {
          this.applyCut(map, cut);
          if (this.lineBoxes().length <= LINES) return;

          this.undoCut();
          map = this.textMap(); /* normalize() invalidated the old node refs */
          cut = this.previousWord(map.text, cut);
        }

        this.restore(); /* no cut fits: hand back to the CSS line-clamp */
      }

     
      lineBoxes() {
        const walker = document.createTreeWalker(this.description, NodeFilter.SHOW_TEXT, null, false);
        const range = document.createRange();
        const lines = [];
        let node;

        while ((node = walker.nextNode())) {
          if (!node.data.trim()) continue; /* hidden text returns no rects anyway */
          range.selectNodeContents(node);

          Array.prototype.forEach.call(range.getClientRects(), (rect) => {
            if (rect.width <= 0 || rect.height <= 0) return;
            const last = lines[lines.length - 1];
        
            if (last && rect.top < last.bottom - 1) {
              last.top = Math.min(last.top, rect.top);
              last.bottom = Math.max(last.bottom, rect.bottom);
            } else {
              lines.push({ top: rect.top, bottom: rect.bottom });
            }
          });
        }

        return lines;
      }

      textMap() {
        const walker = document.createTreeWalker(this.description, NodeFilter.SHOW_TEXT, null, false);
        const parts = [];
        let text = '';
        let node;

        while ((node = walker.nextNode())) {
          if (!node.data.length) continue;
          parts.push({ node: node, start: text.length });
          text += node.data;
        }

        return { parts: parts, text: text };
      }

      position(map, index) {
        for (let i = map.parts.length - 1; i >= 0; i--) {
          if (index >= map.parts[i].start) {
            return { node: map.parts[i].node, offset: index - map.parts[i].start };
          }
        }
        return null;
      }

    
      lastIndexWithin(map, limit) {
        const range = document.createRange();
        let low = 0;
        let high = map.text.length;
        let best = 0;

        while (low <= high) {
          const mid = (low + high) >> 1;
          const at = this.position(map, mid);
          if (!at) break;

          range.setStart(this.description, 0);
          range.setEnd(at.node, Math.min(at.offset, at.node.data.length));
          const rect = range.getBoundingClientRect();

          if (rect.height === 0 || rect.bottom <= limit) {
            best = mid;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }

        return best;
      }

      wordBoundary(text, index) {
        let i = Math.min(index, text.length);
        if (i < text.length && !/\s/.test(text.charAt(i))) {
          while (i > 0 && !/\s/.test(text.charAt(i - 1))) i--;
        }
        while (i > 0 && /\s/.test(text.charAt(i - 1))) i--;
        return i;
      }

      previousWord(text, index) {
        let i = index;
        while (i > 0 && !/\s/.test(text.charAt(i - 1))) i--;
        while (i > 0 && /\s/.test(text.charAt(i - 1))) i--;
        return i;
      }

      applyCut(map, index) {
        const at = this.position(map, index);
        if (!at) return;

        const tail = at.offset < at.node.data.length ? at.node.splitText(at.offset) : null;
        if (tail) this.hide(tail);
        this.hideEverythingAfter(tail || at.node);

        this.more = document.createElement('span');
        this.more.className = MORE;
        this.more.appendChild(document.createTextNode('… '));
        this.more.appendChild(this.button);
        at.node.parentNode.insertBefore(this.more, at.node.nextSibling);
      }

    
      hideEverythingAfter(node) {
        let current = node;
        while (current && current !== this.description) {
          let sibling = current.nextSibling;
          while (sibling) {
            const next = sibling.nextSibling;
            this.hide(sibling);
            sibling = next;
          }
          current = current.parentNode;
        }
      }

      hide(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          node.classList.add(CLIP);
          return;
        }
        if (node.nodeType !== Node.TEXT_NODE || !node.data.trim()) return;

        const span = document.createElement('span');
        span.className = SPLIT + ' ' + CLIP;
        node.parentNode.insertBefore(span, node);
        span.appendChild(node);
      }


      undoCut() {
        if (this.more) {
          this.appendChild(this.button); /* back to its own line, below the text */
          if (this.more.parentNode) this.more.parentNode.removeChild(this.more);
          this.more = null;
        }

        Array.prototype.forEach.call(this.description.querySelectorAll('.' + CLIP), (node) => {
          node.classList.remove(CLIP);
        });

        Array.prototype.forEach.call(this.description.querySelectorAll('.' + SPLIT), (span) => {
          const parent = span.parentNode;
          while (span.firstChild) parent.insertBefore(span.firstChild, span);
          parent.removeChild(span);
        });

        this.description.normalize();
      }

      restore() {
        this.undoCut();
        this.description.classList.remove('is-clipped');
        this.description.style.maxHeight = ''; 
      }
    }
  );
}
